import {getGameDayKey} from '../date/getGameDayKey';

/**
 * Расписание ежедневного промо-матча против ИИ («Битва с AI Claude Opus»).
 *
 * Время задано по Москве, и Москва здесь — ФИКСИРОВАННОЕ смещение UTC+3:
 * перехода на летнее время в России нет, а сервер и клиенты живут в разных
 * часовых поясах, так что опираться на локальное время процесса нельзя —
 * окно считается от UTC.
 *
 *  - create (20:50) — сервер создает лобби, в него уже можно входить;
 *  - opens  (21:00) — раньше этого момента обратный отсчет до старта не
 *    взводится (см. aiPromoStartTimer.js), игрокам показывается «старт не
 *    позже …»;
 *  - cutoff (23:00) — если к этому моменту в лобби нет ни одного живого
 *    игрока и фаза банов не началась, лобби удаляется, день закрыт.
 *
 * Модуль чистый: время приходит аргументом, сервер сам решает, когда
 * спрашивать.
 */

export const AI_PROMO_MSK_OFFSET_MS = 3 * 60 * 60 * 1000;

export const AI_PROMO_SCHEDULE = {
  create: {hour: 20, minute: 50},
  opens: {hour: 21, minute: 0},
  cutoff: {hour: 23, minute: 0},
};

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

const toTimestamp = (now) => (now instanceof Date ? now.getTime() : now);

const getTimeOfDayMs = ({hour, minute}) => (hour * 60 + minute) * MINUTE_MS;

/**
 * Начало московских суток, в которые попадает момент now (UTC-метка
 * московской полуночи). Сдвигаем момент в «московское UTC», отрезаем время
 * суток и сдвигаем обратно.
 */
const getMskDayStartAt = (timestamp) => {
  const mskTimestamp = timestamp + AI_PROMO_MSK_OFFSET_MS;
  return Math.floor(mskTimestamp / DAY_MS) * DAY_MS - AI_PROMO_MSK_OFFSET_MS;
};

/**
 * Окно промо-матча на московские сутки, в которые попадает now.
 *
 * dayKey — общий ключ игрового дня (getGameDayKey), взятый от момента
 * создания лобби: игровые сутки сменяются в 05:00 МСК, поэтому весь вечер
 * 20:50–23:00 (и сам матч после него) лежит в одном игровом дне, и запись дня
 * в базе (AiPromoDay) совпадает с днем остальных ежедневных механик.
 *
 * После московской полуночи окно — уже следующего вечера: матч, начавшийся
 * накануне, сервер ведет по своему лобби и записи дня, а не по этому окну.
 *
 * @param {number|Date} now - текущий момент (мс)
 * @param {Object} [schedule] - расписание (по умолчанию AI_PROMO_SCHEDULE);
 *   сдвиг окна для пилота передается сюда же. Часы считаются от московской
 *   полуночи этих суток, так что hour >= 24 уходит на следующие сутки
 * @returns {{dayKey: string, createAt: number, opensAt: number, cutoffAt: number}}
 */
export const getAiPromoWindow = (now, schedule = AI_PROMO_SCHEDULE) => {
  const dayStartAt = getMskDayStartAt(toTimestamp(now));
  const createAt = dayStartAt + getTimeOfDayMs(schedule.create);
  return {
    dayKey: getGameDayKey(createAt),
    createAt,
    opensAt: dayStartAt + getTimeOfDayMs(schedule.opens),
    cutoffAt: dayStartAt + getTimeOfDayMs(schedule.cutoff),
  };
};

/**
 * Можно ли сейчас держать лобби дня: от создания (включительно) до отсечки
 * (не включая). В 23:00 ровно лобби уже не создается.
 */
export const checkIfWithinAiPromoCreationWindow = (now, window) => {
  if (!window) {
    return false;
  }
  const timestamp = toTimestamp(now);
  return window.createAt <= timestamp && timestamp < window.cutoffAt;
};
